//@author: devin
import { DataAccess } from "../dataaccess/data.access";
import { S25Util } from "../util/s25-util";
import { Timeout } from "../decorators/timeout.decorator";
import { Cache, Invalidate } from "../decorators/cache.decorator";

/** File list / download cache TTL for tile grids and repeated logo loads (ms). */
const FILESTORE_GET_CACHE_MS = 60_000;

export interface FileItem {
    itemId: string;
    itemName: string;
    itemTypeId: number;
    objectTypeId: string | number;
    objectId: string | number;
    fileKey?: string;
    createDt?: string;
    custAtrbId?: number;
}

/** One row from `file_list.file` in `/fs/list/file_list.json` before mapping to {@link FileItem}. */
export interface FileListJsonItem {
    external_id: string | number;
    file_name: string;
    create_dt?: string;
    cust_atrb_id?: string | number;
}

export interface AttachmentItem {
    is_native?: boolean;
    itemId?: string;
    itemName?: string;
    itemTypeId?: number;
    objectId?: string;
    objectTypeId?: number;
    fileKey?: string;
    isNewItem?: boolean;
}

export class AttachmentService {
    public static sanitizeFilenameSpaces(name: string | undefined): string | undefined {
        if (!name) return name;
        // Replace a set of Unicode space characters (including narrow no‑break space) with normal space
        const unicodeSpaces = /[\u00A0\u1680\u180E\u2000-\u200A\u202F\u205F\u3000]/g;
        let sanitized = name.replace(unicodeSpaces, " ");
        // Collapse whitespace and trim
        sanitized = sanitized.replace(/\s+/g, " ").trim();
        return sanitized || name;
    }

    /**
     * Object type IDs for filestore attachments
     */
    public static readonly ObjectType = {
        EVENT: 1,
        ORGANIZATION: 2,
        CONTACT: 3,
        SPACE: 4,
        ORG_NEWS_ITEM: 22, // Organization news article image
    } as const;

    @Cache({ targetName: "AttachmentService", expireTimeMs: FILESTORE_GET_CACHE_MS })
    @Timeout
    public static getFileList(
        objectTypeId: number,
        objectId: number | undefined,
        fileKey?: string,
    ): Promise<FileItem[] | undefined> {
        if (!objectId) return Promise.resolve(undefined);
        return new Promise((resolve) => {
            DataAccess.get(
                DataAccess.injectCaller(
                    "/fs/list/file_list.json?" +
                        DataAccess.composeParam("object_type", objectTypeId, true) +
                        DataAccess.composeParam("object_id", objectId) +
                        (fileKey ? "&file_key=" + encodeURIComponent(fileKey) : ""),
                    "AttachmentDao.getFileList",
                ),
            ).then(
                function (data) {
                    resolve(
                        AttachmentService.composeFileListObject(
                            S25Util.prettifyJson(data, null, { file: true }).file_list.file,
                            objectTypeId,
                            objectId,
                            fileKey,
                        ),
                    );
                },
                function () {
                    resolve(undefined);
                },
            );
        });
    }

    public static composeFileListObject(
        list: FileListJsonItem[],
        objectTypeId: string | number,
        objectId: number,
        fileKey?: string,
    ): FileItem[] | undefined {
        if (!list) {
            return undefined;
        }
        return list.map((row: FileListJsonItem): FileItem => {
            const custAtrbId = S25Util.parseInt(row.cust_atrb_id) || undefined;
            return {
                itemId: String(row.external_id),
                itemName: String(row.file_name),
                itemTypeId: 1000 + S25Util.parseInt(objectTypeId),
                objectTypeId: objectTypeId, // parent typeId, i.e. event=1, space=4, etc.
                objectId: objectId, // parent id, i.e. eventId, spaceId, etc.
                fileKey: fileKey,
                createDt: row.create_dt ?? undefined,
                custAtrbId,
            };
        });
    }

    public static getFileUrl(
        objectTypeId: number,
        objectId: number,
        itemName: string,
        display?: string,
        fileKey?: string,
        caId?: number,
    ) {
        return DataAccess.getUrl(
            DataAccess.injectCaller(
                "/fs/download/download_file.xml?" +
                    DataAccess.composeParam("object_type", objectTypeId, true) +
                    DataAccess.composeParam("object_id", objectId) +
                    DataAccess.composeParamUrlEncode("file_name", itemName) +
                    (display ? DataAccess.composeParam("display", display) : "") +
                    (fileKey ? "&file_key=" + encodeURIComponent(fileKey) : "") +
                    (caId ? "&atrb_id=" + caId : ""),
                "AttachmentDao.getFileUrl",
            ),
        );
    }

    @Cache({ targetName: "AttachmentService", expireTimeMs: FILESTORE_GET_CACHE_MS })
    public static getFile(
        objectTypeId: number,
        objectId: number,
        itemName: string,
        display?: string,
        fileKey?: string,
        caId?: number,
    ): Promise<Blob | undefined> {
        return DataAccess.get(this.getFileUrl(objectTypeId, objectId, itemName, display, fileKey, caId), {
            responseType: "blob",
        }).then(
            (resp): Blob => resp,
            (): undefined => undefined,
        );
    }

    public static getFileIfExists(
        objectTypeId: number,
        objectId: number,
        itemName: string,
        display?: string,
        fileKey?: string,
        caId?: number,
    ): Promise<Blob | null> {
        return this.getFileList(objectTypeId, objectId, fileKey).then((list) => {
            if (list && list.length > 0) {
                if (list.some((item) => item.itemName === itemName)) {
                    return this.getFile(objectTypeId, objectId, itemName, display, fileKey, caId).then(
                        (b) => b ?? null,
                    );
                }
            }
            return null;
        });
    }

    public static async getFileTextIfExists(
        objectTypeId: number,
        objectId: number,
        itemName: string,
        display?: string,
        fileKey?: string,
        caId?: number,
    ): Promise<string> {
        const blob = await this.getFileIfExists(objectTypeId, objectId, itemName, display, fileKey, caId);
        return (await blob?.text()) ?? "";
    }

    //writes:
    @Invalidate({ serviceName: "AttachmentService", methodName: "getFileList" })
    public static postFile(
        objectTypeId: number,
        objectId: number | undefined,
        file: any,
        fileName?: string,
        caId?: number,
        fileKey?: string,
    ) {
        if (!objectId) return;
        const sanitizedFileName = AttachmentService.sanitizeFilenameSpaces(fileName);
        if (typeof file === "string" && fileName) {
            const blob = new Blob([file], { type: "text/plain" });
            file = new File([blob], sanitizedFileName ?? fileName, { type: "text/plain" });
        } else if (file instanceof File && sanitizedFileName && file.name !== sanitizedFileName) {
            // Re-wrap the File so the name sent to the server is sanitized as well
            file = new File([file], sanitizedFileName, { type: file.type });
        }

        let url = `/fs/upload/upload_file.json?object_type=${objectTypeId}&object_id=${objectId}`;
        if (caId) {
            url += `&atrb_id=${caId}`;
        }
        if (fileKey) {
            url += "&file_key=" + encodeURIComponent(fileKey);
        }
        return DataAccess.postFile(DataAccess.injectCaller(url, "AttachmentDao.postFile"), file);
    }

    @Invalidate({ serviceName: "AttachmentService", methodName: "getFileList" })
    public static delFile(
        objectTypeId: number,
        objectId: number | undefined,
        itemName: string | undefined,
        fileKey?: string,
    ) {
        if (objectId == null || itemName == null) return;
        return DataAccess.delete(
            DataAccess.injectCaller(
                "/fs/delete/delete_file.json?" +
                    DataAccess.composeParamUrlEncode("file_name", itemName, true) +
                    DataAccess.composeParam("object_type", objectTypeId) +
                    DataAccess.composeParam("object_id", objectId) +
                    (fileKey ? "&file_key=" + encodeURIComponent(fileKey) : ""),
                "AttachmentDao.delFile",
            ),
        );
    }

    // delete mulitple files
    public static delFiles(objectTypeId: number, objectIds: number[], attrId: number) {
        const url = `/fs/delete/delete_files.json?`;

        const payLoad = {
            object_file_lists: {
                object_file_list: objectIds.map(function (id) {
                    return {
                        object_type: objectTypeId,
                        object_id: id,
                        cust_atrb_id: attrId,
                    };
                }),
            },
        };

        return DataAccess.post(url, payLoad);
    }

    public static FILE_EXTENSION_REGEX: string =
        "\\.pdf|\\.txt|\\.rtf|\\.jpg|\\.jpeg|\\.png|\\.gif|\\.doc|\\.docx|\\.csv|\\.xsl|\\.xlsx|\\.msg|\\.pptx|\\.ppt|\\.vsdx";

    public static convertFileItemToAttachmentItem(fileItem: FileItem): AttachmentItem {
        return {
            ...fileItem,
            objectId: fileItem.objectId.toString(),
            objectTypeId:
                typeof fileItem.objectTypeId === "string" ? parseInt(fileItem.objectTypeId) : fileItem.objectTypeId,
        };
    }
}
